<template>
    <div class="ini-invoice-list">
        <div class="listview listview--bordered">
            <ini-invoice-item v-for="invoice in entries" 
                              :key="invoice.record_id"
                              v-bind="invoice">
            </ini-invoice-item>
            
            <!-- Loading more state -->
            <div v-if="isLoadingMore" 
                 class="pagination-state loading-more"
                 role="status"
                 aria-live="polite"
                 aria-label="Loading more invoices">
                <div class="pagination-spinner" aria-hidden="true"></div>
                <span class="pagination-text">{{ $t('LBL_LOADING') }}</span>
            </div>
            
            <!-- Invisible sentinel element for intersection observer -->
            <div ref="scrollSentinel" 
                 class="scroll-sentinel" 
                 aria-hidden="true">
            </div>
        </div>
    </div>
</template>

<script>
    import IniInvoiceItem from '@/components/Invoice/List/ListItem'

    export default {
        props: {
            title: {
                type: String,
                default: 'Invoice List'
            },
            entries: {
                type: Array,
                default: () => []
            }
        },

        components:{
            IniInvoiceItem
        },

        data() {
            return {
                observer: null,
                sentinel: null,
                retryCount: 0,
                maxRetries: 3,
                isRetrying: false
            }
        },

        computed: {
            hasMoreData() {
                const list = this.$store.getters.getList('Invoice')
                return list && list.pagination && list.pagination.hasMoreData || false
            },
            
            isLoadingMore() {
                const list = this.$store.getters.getList('Invoice')
                return list && list.pagination && list.pagination.isLoadingMore || false
            }
        },

        mounted() {
            this.$nextTick(() => {
                this.initializeInfiniteScroll()
            })
        },

        beforeDestroy() {
            this.cleanupInfiniteScroll()
        },

        methods: {
            initializeInfiniteScroll() {
                // Create sentinel element
                this.createSentinel()
                
                // Initialize observer
                this.setupIntersectionObserver()
            },

            createSentinel() {
                // Sentinel is already in template as ref
                this.sentinel = this.$refs.scrollSentinel
                
                if (!this.sentinel) {
                    console.error('Scroll sentinel element not found')
                }
            },

            setupIntersectionObserver() {
                // Check browser support
                if (!window.IntersectionObserver) {
                    console.error('IntersectionObserver not supported')
                    return
                }

                const options = {
                    root: null, // Use viewport as root
                    rootMargin: '100px 0px', // Trigger 100px before bottom
                    threshold: 0.1 // Trigger when 10% of sentinel is visible
                }

                this.observer = new IntersectionObserver(
                    this.handleIntersection.bind(this),
                    options
                )

                this.observer.observe(this.sentinel)
            },

            handleIntersection(entries) {
                const entry = entries[0]
                
                if (entry.isIntersecting) {
                    this.attemptLoadMore(true)
                }
            },

            attemptLoadMore(isNewAttempt = false) {
                // Check all conditions before loading
                if (!this.canLoadMore()) {
                    return
                }

                // Only reset retry state on new scroll attempts, not retries
                if (isNewAttempt) {
                    this.retryCount = 0
                    this.isRetrying = false
                }
                
                // Dispatch store action
                    this.$store.dispatch('fetchNextPage', {
                    module: 'Invoice'
                }).catch(error => {
                    this.handleLoadError(error)
                })
            },

            canLoadMore() {
                return this.hasMoreData && 
                       !this.isLoadingMore && 
                       !this.isRetrying &&
                       this.retryCount < this.maxRetries
            },

            handleLoadError(error) {
                console.error('Failed to load more invoices:', error)
                this.retryCount++
                
                if (this.retryCount < this.maxRetries) {
                    this.isRetrying = true
                    
                    // Retry after delay with exponential backoff
                    setTimeout(() => {
                        // Clear the pagination error just before retry attempt
                        this.$store.commit('setPaginationError', {module: 'Invoice', error: null})
                        
                        this.isRetrying = false
                        
                        if (this.canLoadMore()) {
                            this.attemptLoadMore() // No parameter = retry attempt (don't reset count)
                        }
                    }, 2000 * this.retryCount) // 2s, 4s, 6s
                } else {
                    // Max retries reached, keep the error state
                    console.error('Max retries reached for pagination')
                    this.isRetrying = false
                }
            },

            cleanupInfiniteScroll() {
                if (this.observer) {
                    this.observer.disconnect()
                    this.observer = null
                }
            }
        }
    }
</script>
<style lang="scss">@import "@/assets/scss/components/Invoice/List/_list.scss";</style>